import React from 'react';
import { FiGlobe, FiTrendingUp, FiCheckCircle, FiUsers, FiShield,  FiBookOpen, FiAward } from 'react-icons/fi';

const AutonomousCollege = () => {
  const benefits = [
    {
      icon: <FiAward className="w-6 h-6" />,
      title: 'Academic Freedom and Flexibility',
      description: 'Autonomous colleges have the freedom to design their own curriculum, decide evaluation methods, and set academic standards. The ERP must support this flexibility without imposing rigid structures that limit this freedom.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Design Your Own Curriculum',
      description: 'Autonomous colleges can create unique programs that meet industry needs and student aspirations. The ERP should support custom course structures, program designs, and credit systems without predefined templates.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Custom Evaluation Methods',
      description: 'Autonomous colleges can design their own evaluation methods—continuous assessment, project-based evaluation, or innovative grading systems. The ERP must adapt to these methods without forcing standard patterns.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Industry-Relevant Programs',
      description: 'One of the biggest advantages of autonomous colleges is the ability to create programs that align with industry needs. The ERP should support quick program design, approvals, and implementation.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Accreditation and Quality Assurance',
      description: 'Autonomous colleges must maintain high standards for accreditation. The ERP should generate comprehensive reports for NAAC, NIRF, and other accreditation bodies while supporting the unique requirements of autonomous status.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Faculty Empowerment',
      description: 'Autonomous colleges empower faculty to contribute to curriculum design, evaluation, and academic governance. The ERP should support faculty participation in academic decision-making.',
      color: 'bg-blue-50'
    }
  ];

  const features = [
    {
      title: 'Flexible Curriculum Management',
      description: 'Create custom program structures without being bound by university templates. Add, remove, or modify courses quickly.'
    },
    {
      title: 'Custom Grading Systems',
      description: 'Design evaluation methods that work for your students and programs. Support continuous assessment, project-based evaluation, or innovative grading models.'
    },
    {
      title: 'Quick Program Approvals',
      description: 'Accelerate program design and approval processes. Bring industry-relevant programs to students faster.'
    },
    {
      title: 'Academic Governance Support',
      description: 'Track academic committee decisions, maintain records, and ensure compliance with autonomous college regulations.'
    },
    {
      title: 'Accreditation Readiness',
      description: 'Generate reports that satisfy NAAC, NIRF, and other accreditation requirements automatically.'
    },
    {
      title: 'Student-Centric Features',
      description: 'Empower students with choices—elective selection, flexible learning pathways, and self-paced learning options.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Autonomous colleges represent the future of higher education in India. These institutions have the freedom to design their own curriculum, determine their own evaluation methods, and set their own academic standards. This autonomy allows them to be innovative, responsive to industry needs, and focused on student outcomes.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        However, <strong className="text-slate-900">with great autonomy comes great responsibility.</strong> Autonomous colleges must maintain rigorous quality standards, meet accreditation requirements, and ensure that their academic freedom translates into better learning outcomes. This requires systems that are as flexible as the institution itself—not rigid, standardized solutions.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        A flexible ERP is essential for autonomous colleges to fully leverage their independence while maintaining accountability. Here's what autonomous colleges need from their ERP.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">100+</div>
          <div className="text-sm text-slate-500">Autonomous colleges in India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">70%</div>
          <div className="text-sm text-slate-500">Faster program innovation with autonomy</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">2X</div>
          <div className="text-sm text-slate-500">Better industry-aligned programs</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Autonomy Requires a Flexible ERP</h2>

      {/* Benefits List */}
      {benefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Key Features Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Autonomous Colleges Need in Their ERP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Standard ERP vs. Flexible ERP for Autonomous Colleges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Standard University ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One-size-fits-all approach</span></li>
              <li className="flex items-start gap-2">• <span>Predefined curriculum templates</span></li>
              <li className="flex items-start gap-2">• <span>Standard grading systems</span></li>
              <li className="flex items-start gap-2">• <span>Slow program design process</span></li>
              <li className="flex items-start gap-2">• <span>Limited flexibility for innovation</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Flexible Autonomous ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Customizable approach</span></li>
              <li className="flex items-start gap-2">• <span>Flexible program design tools</span></li>
              <li className="flex items-start gap-2">• <span>Custom evaluation methods</span></li>
              <li className="flex items-start gap-2">• <span>Quick program implementation</span></li>
              <li className="flex items-start gap-2">• <span>Supports innovation and autonomy</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Autonomous Colleges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎨</div>
            <h4 className="font-bold text-slate-900 text-sm">Flexible Curriculum</h4>
            <p className="text-xs text-slate-600">Design programs without predefined templates</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Custom Evaluation</h4>
            <p className="text-xs text-slate-600">Design your own grading and assessment methods</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Quick Program Design</h4>
            <p className="text-xs text-slate-600">Accelerate program creation and approval</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Accreditation Ready</h4>
            <p className="text-xs text-slate-600">Generate NAAC and NIRF compliant reports</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👨‍🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Faculty Empowerment</h4>
            <p className="text-xs text-slate-600">Support faculty participation in academic governance</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">Student-Centric</h4>
            <p className="text-xs text-slate-600">Empower students with choices and flexible pathways</p>
          </div>
        </div>
      </div>

      {/* The Autonomy Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Autonomy Journey</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🚀</div>
            <h4 className="font-bold text-slate-900 text-sm">Design</h4>
            <p className="text-xs text-slate-600">Create innovative programs and curriculum</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Implement</h4>
            <p className="text-xs text-slate-600">Launch programs quickly with flexible systems</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Track</h4>
            <p className="text-xs text-slate-600">Monitor outcomes and maintain quality standards</p>
          </div>
        </div>
      </div>

      {/* The Bigger Picture */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Real Value of Autonomy</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Autonomy is not just about freedom—it's about responsibility. Autonomous colleges have the opportunity to create truly innovative programs, respond to industry needs quickly, and develop curricula that prepare students for the future. But this requires systems that support innovation, not systems that limit it.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          When autonomous colleges use the right ERP, they can design programs faster, implement changes more easily, and maintain high quality standards. They can experiment, innovate, and lead the transformation of higher education in India.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Your autonomy should be empowered, not limited by your systems.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Flexible ERP for Autonomous Colleges</h3>
        <p className="text-indigo-800 leading-relaxed">
          Autonomous colleges are leading the transformation of Indian higher education. They have the freedom to innovate, the flexibility to respond to industry needs, and the responsibility to maintain high quality standards. They need systems that match their ambition.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          A flexible ERP designed for autonomous colleges supports curriculum innovation, custom evaluation methods, quick program implementation, and accreditation readiness. It empowers colleges to exercise their autonomy fully while maintaining accountability.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon is built for autonomous colleges.</strong> Flexible. Innovative. Empowering. Ready to support your autonomy journey.
        </p>
      </div>
    </>
  );
};

export default AutonomousCollege;